import { trackEvent } from '@/shared/utils/analyticsEvents';

/**
 * Where the OOM was detected in the app.
 *
 * - `worker.loadFile` : thrown by the core worker while fetching/parsing a grid
 * - `coreError`       : surfaced to React via the `coreError` event bus
 * - `window.error`    : top-level synchronous window error (often WASM trap)
 * - `window.rejection`: top-level unhandled promise rejection
 * - `sticky`          : prior-session OOM replayed via sessionStorage flag
 */
export type OomSurface = 'worker.loadFile' | 'coreError' | 'window.error' | 'window.rejection' | 'sticky';

interface OomContext {
  surface: OomSurface;
  source?: string;
  isMobile?: boolean;
  uuid?: string;
  sizeBytes?: number;
  error?: Error | unknown;
}

/**
 * Emit a single, consistent Mixpanel event whenever an out-of-memory condition
 * is detected. Keep the event name stable (`oom.detected`) so dashboards can
 * slice by `surface` and `isMobile` to see where the limit is hit most.
 */
export const trackOomDetected = (ctx: OomContext): void => {
  const viewportWidth =
    typeof window !== 'undefined' && typeof window.innerWidth === 'number' ? window.innerWidth : undefined;
  const viewportHeight =
    typeof window !== 'undefined' && typeof window.innerHeight === 'number' ? window.innerHeight : undefined;
  const userAgent =
    typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string' ? navigator.userAgent : undefined;

  let message: string | undefined;
  let stackExcerpt: string | undefined;
  if (ctx.error instanceof Error) {
    message = ctx.error.message;
    // Trim stacks so a single event payload stays small (Mixpanel truncates
    // long string values). The first 10 frames are enough to distinguish
    // `__wbindgen_malloc` OOMs from other WASM traps.
    stackExcerpt = ctx.error.stack?.split('\n').slice(0, 10).join('\n');
  } else if (ctx.error !== undefined) {
    message = String(ctx.error);
  }

  trackEvent('oom.detected', {
    surface: ctx.surface,
    source: ctx.source,
    isMobile: ctx.isMobile,
    uuid: ctx.uuid,
    sizeBytes: ctx.sizeBytes,
    viewportWidth,
    viewportHeight,
    userAgent,
    message,
    stackExcerpt,
  });
};
